/**
 * Two decisions about session state, in one place because they are the same
 * decision seen from both ends: **an absent credential is not a rejected one.**
 *
 * Conflating those is what produced the bug this module exists to prevent. The
 * app used to fetch the role catalogue and the Slack status on mount, before
 * Clerk had produced a token, so both requests left with no `Authorization`
 * header. The router answers those `401 {"detail":"Not authenticated"}`, and the
 * response interceptor read that as *our* session failing — setting the flag
 * that renders "Your session could not be verified, so you've been signed out"
 * and, on any path other than `/login`, redirecting there. A signed-in user got
 * signed out of a valid session on every full page load, because of a request
 * the app knowingly sent unauthenticated.
 */

/**
 * Whether a request issued right now would carry a credential the API accepts.
 *
 * The gate for anything authenticated the app fetches on its own initiative. It
 * is deliberately *not* "is the user signed in" — it is "have we got something
 * to send", which is the question the API cares about.
 *
 * `devBypass` short-circuits because that session is an httpOnly cookie the
 * browser attaches itself, so there is no token to wait for and Clerk may never
 * load at all on that path.
 *
 * @param {object} state
 * @param {boolean} state.clerkLoaded  clerk-js has finished initialising.
 * @param {boolean} state.isSignedIn   Clerk reports an active session.
 * @param {boolean} state.devBypass    a password-gated dev session cookie exists.
 * @returns {boolean}
 */
export function credentialsAvailable({ clerkLoaded, isSignedIn, devBypass }) {
  if (devBypass) return true;
  return Boolean(clerkLoaded) && Boolean(isSignedIn);
}

/**
 * Whether a 401 is evidence that our own session was rejected.
 *
 * Three things can produce a 401, and only one of them means "sign the user out":
 *
 *   1. We sent a credential and it was refused — expired, revoked, wrong issuer.
 *      That is a session rejection.
 *   2. We sent nothing, because there was nothing to send. The router is simply
 *      doing its job. Not a rejection: there was no session to reject.
 *   3. An upstream integration's 401 passed through us. Not ours either, and the
 *      `detail` test is what separates it — without it one misconfigured
 *      integration bounced users to `/login` in a loop.
 *
 * @param {object} evidence
 * @param {string} [evidence.detail]        the API's `detail` string, if any.
 * @param {boolean} evidence.sentCredential the request carried a credential.
 * @returns {boolean}
 */
export function isSessionRejection({ detail, sentCredential }) {
  if (!sentCredential) return false;
  const text = typeof detail === 'string' ? detail : '';
  if (!text) return true;
  return /token|authent|session|signing key|issuer|expired|not authenticated/i.test(text);
}
